import { sha256Fallback } from "./sha256";
import { useCallback, useRef, useState } from "react";
import { getConsoleConnections, type ConsoleConnections } from "./api";

/** Namespace UI state by endpoint/account without putting bearer tokens in keys. */
export function workbenchScope(connection: Pick<ConsoleConnections, "controlBase" | "controlToken"> = getConsoleConnections()): string {
  const { controlBase, controlToken } = connection;
  // A credential fingerprint separates accounts, not a security/encryption
  // boundary. Never expose the raw bearer in a storage key. Do not migrate the
  // old FNV32 scope: collisions cannot be safely attributed to an account.
  return "swarm.workbench:v2:" + encodeURIComponent(controlBase) + ":" + sha256Fallback(new TextEncoder().encode(controlToken));
}

type SavedStorage = Storage | (() => Storage);
function resolveStorage(storage?: SavedStorage) { return typeof storage === "function" ? storage() : storage ?? localStorage; }
export function readSaved<T>(key: string, fallback: T, storage?: SavedStorage): T {
  try {
    const raw = resolveStorage(storage).getItem(key);
    return raw === null ? fallback : JSON.parse(raw) as T;
  } catch { return fallback; }
}

export function saveValue(key: string, value: unknown, storage?: SavedStorage): boolean {
  try { resolveStorage(storage).setItem(key, JSON.stringify(value)); return true; }
  catch { return false; }
}

/** Callers changing the key must remount, to avoid writing another session's state. */
export function useSavedState<T>(key: string, fallback: T, validate?: (value: unknown) => T) {
  const [value, setValue] = useState<T>(() => {
    const stored = readSaved<unknown>(key, fallback);
    return validate ? validate(stored) : stored as T;
  });
  const [saved, setSaved] = useState(true);
  const current = useRef(value);
  current.current = value;
  const update = useCallback((next: T | ((previous: T) => T)) => {
    const resolved = typeof next === "function" ? (next as (old: T) => T)(current.current) : next;
    current.current = resolved;
    setSaved(saveValue(key, resolved));
    setValue(resolved);
  }, [key]);
  return [value, update, saved] as const;
}

export function newMessageId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
}

export const defaultPanels = { left: true, right: false, leftWidth: 260, rightWidth: 320 };
export function parsePanels(value: unknown): typeof defaultPanels {
  if (!value || typeof value !== "object") return defaultPanels;
  const data = value as Record<string, unknown>;
  const width = (key: "leftWidth" | "rightWidth", min: number, max: number) => typeof data[key] === "number" && Number.isFinite(data[key]) ? Math.min(max, Math.max(min, data[key] as number)) : defaultPanels[key];
  return { left: typeof data.left === "boolean" ? data.left : true, right: typeof data.right === "boolean" ? data.right : false, leftWidth: width("leftWidth", 220, 480), rightWidth: width("rightWidth", 220, 480) };
}
export function parseScroll(value: unknown): { top: number; follow: boolean } | null {
  if (!value || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  return typeof data.top === "number" && Number.isFinite(data.top) && data.top >= 0 && typeof data.follow === "boolean" ? { top: data.top, follow: data.follow } : null;
}
